import { cva, VariantProps } from "class-variance-authority";
import clsx from "clsx";
import {
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle,
  HiOutlineExclamationTriangle,
  HiOutlineInformationCircle,
  HiXMark,
} from "react-icons/hi2";

const iconMap = {
  success: HiOutlineCheckCircle,
  warning: HiOutlineExclamationTriangle,
  error: HiOutlineExclamationCircle,
  info: HiOutlineInformationCircle,
};

const AlertVariants = cva(
  "relative pl-6 pr-12 inline-flex items-center gap-x-1 justify-center font-medium rounded-lg pointer-events-auto flex w-full max-w-xl rounded-lg border-l-[6px] bg-white shadow-md",
  {
    variants: {
      size: {
        sm: "text-xs py-3",
        md: "text-sm py-6",
      },
      color: {
        info: "bg-brand-primary-subtle text-brand-primary border-brand-primary",
        success: "bg-success-primary text-success border-success",
        warning: "bg-warning-primary text-warning border-warning",
        error: "bg-error-primary text-error border-error",
      },
    },
    defaultVariants: {
      size: "md",
      color: "info",
    },
  },
);

const AlertIconVariants = cva("rounded-full flex items-center justify-center", {
  variants: {
    size: {
      sm: "text-xs w-8 h-8",
      md: "text-sm w-12 h-12 p-3",
    },
    color: {
      info: "bg-brand-primary-subtle text-brand-primary border-brand-primary-subtle",
      success: "bg-success-primary text-success border-success-subtle",
      warning: "bg-warning-primary text-warning border-warning-subtle",
      error: "bg-error-primary text-error border-error-subtle",
    },
  },
  defaultVariants: {
    size: "md",
    color: "info",
  },
});

type AlertProps = VariantProps<typeof AlertVariants> & {
  heading?: string;
  onClose?: () => void;
  isCloseable?: boolean;
  isVisible?: boolean;
  children?: React.ReactNode;
};

const Alert = ({
  size = "md",
  color = "info",
  heading,
  isVisible,
  onClose,
  isCloseable = true,
  children,
}: AlertProps) => {
  const IconComponent = iconMap[color!];

  return (
    <div className={clsx(AlertVariants({ size, color }), isVisible ? "animate-enter" : "animate-leave")}>
      <div className="flex-1">
        <div className={clsx("flex gap-3", children ? "items-start" : "items-center")}>
          <div className={clsx(AlertIconVariants({ size, color }))}>
            <IconComponent className={size === "sm" ? "h-4 w-4" : "h-6 w-6"} />
          </div>
          <div className={"flex flex-1 flex-col gap-1.5"}>
            <p className={size === "sm" ? "text-sm font-medium" : "text-base font-semibold"}>{heading}</p>
            {children && <p className="break-words text-sm font-normal text-quaternary">{children}</p>}
          </div>
        </div>
      </div>
      {isCloseable && (
        <div className={"absolute right-6 top-4.5"}>
          <button
            onClick={onClose}
            className="text-fg-senary focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-input"
          >
            <div className={"-m-3 p-3"}>
              <HiXMark className="h-5 w-5" aria-hidden="true" />
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

export default Alert;
